import React, { createContext, useContext, useReducer, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../api';

// Estados da autenticação
const authReducer = (state, action) => {
  switch (action.type) {
    case 'LOGIN_SUCCESS':
      return {
        ...state,
        isAuthenticated: true,
        user: action.payload.user,
        token: action.payload.token,
        loading: false,
        error: null
      };
    case 'LOGOUT':
      return {
        ...state,
        isAuthenticated: false,
        user: null,
        token: null,
        loading: false,
        error: null
      };
    case 'SET_LOADING':
      return {
        ...state,
        loading: action.payload
      };
    case 'SET_ERROR':
      return {
        ...state,
        error: action.payload,
        loading: false
      };
    case 'CLEAR_ERROR':
      return {
        ...state,
        error: null
      };
    case 'UPDATE_USER':
      return {
        ...state,
        user: action.payload
      };
    default:
      return state;
  }
};

// Estado inicial
const initialState = {
  isAuthenticated: false,
  user: null,
  token: localStorage.getItem('token'),
  loading: true,
  error: null
};

// Criar contexto
const AuthContext = createContext();

// Provider do contexto
export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);
  const queryClient = useQueryClient();
  const isInitialized = useRef(false);

  // Configurar token no axios quando o estado mudar
  useEffect(() => {
    if (state.token) {
      api.defaults.headers.common['Authorization'] = `Bearer ${state.token}`;
      localStorage.setItem('token', state.token);
    } else {
      delete api.defaults.headers.common['Authorization'];
      localStorage.removeItem('token');
    }
  }, [state.token]);

  // Verificar token salvo no localStorage ao carregar a aplicação
  const { data: userData, isLoading: isLoadingUser, error: userError } = useQuery({
    queryKey: ['auth-me'],
    queryFn: async () => {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No token found');
      }
      
      try {
        const response = await api.get('/auth/me');
        return response.data.data.user;
      } catch (error) {
        localStorage.removeItem('token');
        delete api.defaults.headers.common['Authorization'];
        throw error;
      }
    },
    enabled: !!localStorage.getItem('token') && !state.isAuthenticated && !isInitialized.current,
    retry: false,
    staleTime: Infinity,
    cacheTime: 0
  });

  // Inicializar estado uma vez
  useEffect(() => {
    if (!isInitialized.current) {
      const token = localStorage.getItem('token');
      
      if (userData && token) {
        dispatch({
          type: 'LOGIN_SUCCESS',
          payload: {
            user: userData,
            token: token
          }
        });
      } else if (userError || !token) {
        dispatch({
          type: 'LOGOUT'
        });
      } else if (!isLoadingUser && !token) {
        dispatch({ type: 'SET_LOADING', payload: false });
      }
      
      if (!isLoadingUser) {
        isInitialized.current = true;
      }
    }
  }, [userData, userError, isLoadingUser]);

  // Mutation para login
  const loginMutation = useMutation({
    mutationFn: async ({ email, senha }) => {
      const response = await api.post('/auth/login', { email, senha });
      return response.data;
    },
    onSuccess: (data) => {
      dispatch({
        type: 'LOGIN_SUCCESS',
        payload: data.data
      });
      queryClient.invalidateQueries(['auth-me']);
    },
    onError: (error) => {
      dispatch({
        type: 'SET_ERROR',
        payload: error.response?.data?.message || 'Erro ao fazer login'
      });
    }
  });

  // Mutation para registro
  const registerMutation = useMutation({
    mutationFn: async (userData) => {
      const response = await api.post('/auth/register', userData);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['users']);
    },
    onError: (error) => {
      dispatch({
        type: 'SET_ERROR',
        payload: error.response?.data?.message || 'Erro ao registrar usuário'
      });
    }
  });

  // Mutation para atualizar perfil
  const updateProfileMutation = useMutation({
    mutationFn: async (profileData) => {
      const response = await api.put('/auth/profile', profileData);
      return response.data;
    },
    onSuccess: (data) => {
      dispatch({
        type: 'UPDATE_USER',
        payload: data.data.user
      });
      queryClient.invalidateQueries(['auth-me']);
    },
    onError: (error) => {
      dispatch({
        type: 'SET_ERROR',
        payload: error.response?.data?.message || 'Erro ao atualizar perfil'
      });
    }
  });

  // Mutation para alterar senha
  const changePasswordMutation = useMutation({
    mutationFn: async (passwordData) => {
      const response = await api.put('/auth/change-password', passwordData);
      return response.data;
    },
    onError: (error) => {
      dispatch({
        type: 'SET_ERROR',
        payload: error.response?.data?.message || 'Erro ao alterar senha'
      });
    }
  });

  // Funções de autenticação
  const login = async (credentials) => {
    dispatch({ type: 'SET_LOADING', payload: true });
    dispatch({ type: 'CLEAR_ERROR' });
    
    try {
      await loginMutation.mutateAsync(credentials);
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const register = async (userData) => {
    dispatch({ type: 'SET_LOADING', payload: true });
    dispatch({ type: 'CLEAR_ERROR' });
    
    try {
      await registerMutation.mutateAsync(userData);
      return true;
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const logout = () => {
    dispatch({ type: 'LOGOUT' });
    queryClient.clear();
    isInitialized.current = false;
  };

  const updateProfile = async (profileData) => {
    dispatch({ type: 'CLEAR_ERROR' });
    
    try {
      await updateProfileMutation.mutateAsync(profileData);
      return true;
    } catch (error) {
      return false;
    }
  };

  const changePassword = async (passwordData) => {
    dispatch({ type: 'CLEAR_ERROR' });
    
    try {
      await changePasswordMutation.mutateAsync(passwordData);
      return true;
    } catch (error) {
      return false;
    }
  };

  const clearError = () => {
    dispatch({ type: 'CLEAR_ERROR' });
  };

  const value = {
    ...state,
    loading: state.loading || isLoadingUser || loginMutation.isPending || registerMutation.isPending,
    login,
    register,
    logout,
    updateProfile,
    changePassword,
    clearError,
    isLoginLoading: loginMutation.isPending,
    isRegisterLoading: registerMutation.isPending,
    isUpdateProfileLoading: updateProfileMutation.isPending,
    isChangePasswordLoading: changePasswordMutation.isPending
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Hook para usar o contexto
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth deve ser usado dentro de AuthProvider');
  }
  return context;
};

export default AuthContext;
